import { useEffect, useId, useMemo, useState, type RefObject } from 'react'

import type { ChartBand } from '~/lib/chart-band'
import { dayStart, formatDayLabel } from '~/lib/dates'
import { daysApart, xFractions, type DayValue, type Domain } from '~/lib/day-value'
import { curveSplitBy, curveThrough, type CurvePoint } from '~/lib/trend-curve'

/*
 * The hero's sparkline. It is scenery rather than a chart, so it goes without chart chrome — no
 * axes, no gridlines, no labels, and no dots marking the days: a single accent curve edge to
 * edge, with a wash beneath it that carries on falling long after the curve has levelled out.
 * Pointing at it names the nearest day and its best in the chart's quiet lower-left, where the
 * oldest — and usually lowest — end of the line leaves room, and only then does a dot appear.
 */

/*
 * The box is drawn in hundredths of itself and stretched to whatever it is given, so the curve
 * runs the full width of a hero however wide that hero came out. Strokes opt out of that
 * stretch, and the dot is an element rather than a circle, so neither is left an ellipse.
 */
const BOX = 100

/** Room at the top for the curve's crest, so a record day is not drawn on the edge. */
const TOP = 5

type Placed<P extends DayValue> = P & CurvePoint

/** The point nearest a horizontal position on a surface sharing the chart's width. */
function horizontalAt(clientX: number, surface: HTMLElement): number {
  const box = surface.getBoundingClientRect()
  // A box with no layout (jsdom) reads as the graph's oldest edge.
  return box.width === 0 ? 0 : Math.max(0, Math.min(BOX, ((clientX - box.left) / box.width) * BOX))
}

/** The point nearest a horizontal position in the chart. */
function nearestAt<P extends DayValue>(points: readonly Placed<P>[], x: number): Placed<P> | null {
  let nearest: Placed<P> | null = null
  for (const point of points) {
    if (nearest === null || Math.abs(point.x - x) < Math.abs(nearest.x - x)) {
      nearest = point
    }
  }
  return nearest
}

/**
 * A quiet fake history that holds the real chart's place while its data arrives. It has no
 * chart semantics: the surrounding loading status says all there is to say until the real
 * dates and values can be drawn.
 */
export function TrendChartSkeleton({
  curveBand = 1,
  className = 'relative',
}: {
  curveBand?: number
  className?: string
}) {
  const id = useId()
  const height = BOX * curveBand
  const fake = [
    [0, 0.78],
    [14, 0.66],
    [29, 0.72],
    [45, 0.48],
    [62, 0.55],
    [78, 0.3],
    [90, 0.38],
    [100, 0.18],
  ].map(([x = 0, share = 0]): CurvePoint => ({
    x,
    y: TOP + share * (height - TOP),
  }))
  const curve = curveThrough(fake)
  const first = fake[0]
  const last = fake[fake.length - 1]

  return (
    <div aria-hidden className={className}>
      <svg
        viewBox={`0 0 ${String(BOX)} ${String(BOX)}`}
        preserveAspectRatio="none"
        className="h-full w-full"
      >
        <defs>
          <linearGradient
            id={`${id}-skeleton-fill`}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2="0"
            y2={BOX}
          >
            <stop offset="0%" stopColor="white" stopOpacity="0.24" />
            <stop offset="55%" stopColor="white" stopOpacity="0.08" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        {first && last ? (
          <path
            d={`${curve} L ${String(last.x)} ${String(BOX)} L ${String(first.x)} ${String(BOX)} Z`}
            fill={`url(#${id}-skeleton-fill)`}
          />
        ) : null}
        <path
          d={curve}
          fill="none"
          stroke="white"
          strokeOpacity="0.28"
          strokeWidth={7}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={curve}
          fill="none"
          stroke="white"
          strokeOpacity="0.52"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  )
}

export function TrendChart<P extends DayValue>({
  points,
  reading,
  label = 'Trend',
  marked = null,
  marksLatest = false,
  rulesOpening = false,
  ruleAt,
  band,
  domain,
  gapDays,
  color = 'var(--color-accent)',
  curveBand = 1,
  className = 'relative',
}: {
  /** Oldest day first, as `dailyBest` hands them over. */
  points: readonly P[]
  /**
   * What the chart says where it is pointed at, and the surface the pointing happens on. Left
   * out, the chart answers no pointer at all — which is what a card wants, where the curve is
   * scenery behind a number and the press belongs to whatever the card leads to.
   */
  reading?: {
    formatValue: (value: number) => string
    formatPoint: (point: P) => string
    /** A larger surface that follows the same horizontal scale as the chart. */
    interactionRef?: RefObject<HTMLElement | null>
  }
  label?: string
  /**
   * A day to mark on the line, for a crosshair the chart does not own. The chart draws the
   * dot because the chart is the only thing that knows where the curve put that day.
   */
  marked?: string | null
  /**
   * Whether the newest reading wears a dot of its own. A line simply stopping is a line that
   * ran out of room; a dot at the end of it is the reading it has got to, which is the one the
   * card is naming above.
   */
  marksLatest?: boolean
  /**
   * Whether the value the stretch opened on is ruled across it, dotted. It is what the shape is
   * read against — above the rule the week went up, below it the week went down — and without
   * it a curve says only that a number moved about.
   */
  rulesOpening?: boolean
  /**
   * A value ruled across the chart at the height the chart plots it, dotted like the opening
   * rule — a target weight the trend is read against. It widens the band the curve is plotted
   * in where it stands outside the readings, so the rule is always on the card.
   */
  ruleAt?: number | undefined
  /**
   * The band the curve is plotted in, where a chart is read against values written on it —
   * the same band those are placed by, so a value is written at the height the curve plots
   * it. Left out, the curve takes a band of its own around the readings, which is what a
   * card or a hero wants of a curve nothing is written on.
   */
  band?: ChartBand | undefined
  /**
   * The stretch of time the width stands for. Left out, it is the readings' own — oldest at
   * one edge and newest at the other, which is what a hero's curve wants. A card that is
   * about the last seven days passes those seven days, so days nobody logged are drawn as
   * the empty space they were.
   */
  domain?: Domain
  /**
   * How many whole days a silence has to run before the line across it is drawn as the guess
   * it is. Left out, nothing is a guess and the curve is one unbroken line.
   */
  gapDays?: number
  /**
   * The colour the curve and its wash draw in. The exercise hero takes the accent, which is
   * what it has always been; a body card brings its own, so a row of them can be told apart
   * at a glance rather than by reading every label.
   */
  color?: string
  /**
   * How much of the chart's height the curve itself is plotted in, from the top. The rest is
   * room for the wash to keep falling through — the curve holds its own band, and what is
   * under it runs on down past whatever the chart is standing over.
   */
  curveBand?: number
  /**
   * Where the chart stands and how tall, which is the caller's business — it has no size of its
   * own. It replaces the box's classes rather than adding to them, because a chart told to be
   * `absolute` and carrying a `relative` of its own would quietly keep the `relative`: it comes
   * later in the sheet and would win. Whatever is passed has to leave the box positioned all the
   * same, since the dot and the reading hang off it.
   */
  className?: string
}) {
  const [pointedX, setPointedX] = useState<number | null>(null)
  // Two charts on one page must not share a gradient or a wipe, so both are named per instance.
  const id = useId()

  const { placed, ruleY } = useMemo(() => {
    const across = xFractions(points, domain)
    const values = [
      ...points.map((point) => point.value),
      ...(ruleAt === undefined ? [] : [ruleAt]),
    ]
    const low = Math.min(...values)
    const high = Math.max(...values)
    // A flat line still needs somewhere to sit; pad the band so it does not hug an edge.
    const pad = (high - low || high || 1) * 0.15
    // The band handed in, where the chart is read against values written on it; else its own.
    const floor = band?.floor ?? low - pad
    const span = band === undefined ? high + pad - floor : band.ceiling - band.floor
    // The floor of the curve's own band, which is above the foot of the chart.
    const curveFloor = BOX * curveBand
    const yOf = (value: number) => TOP + (1 - (value - floor) / span) * (curveFloor - TOP)
    return {
      placed: points.map((point, index): Placed<P> => ({
        ...point,
        x: (across[index] ?? 0) * BOX,
        y: yOf(point.value),
      })),
      ruleY: ruleAt === undefined ? null : yOf(ruleAt),
    }
  }, [band, curveBand, domain, points, ruleAt])

  const curve = curveThrough(placed)
  /*
   * The line, split into what was logged and what is being inferred between two logs. A body
   * weight is continuous whether or not anybody wrote it down, so the stretch is still drawn —
   * but drawn as a guess, because a solid line across a fortnight of silence is the chart
   * claiming a fortnight of weigh-ins.
   */
  const drawn = useMemo(() => {
    if (gapDays === undefined) {
      return { solid: curve, guessed: '' }
    }
    return curveSplitBy(placed, (index) => {
      const from = placed[index]
      const to = placed[index + 1]
      if (from === undefined || to === undefined) {
        return false
      }
      // Whole days apart rather than hours apart — see `daysApart`.
      return daysApart(from.day, to.day) > gapDays
    })
  }, [curve, gapDays, placed])
  const first = placed[0]
  const last = placed[placed.length - 1]
  const hovered = pointedX === null || !reading ? null : nearestAt(placed, pointedX)
  // The dot marks whichever day is being pointed at, whoever is doing the pointing.
  const marker =
    hovered ?? (marked === null ? null : (placed.find((p) => p.day === marked) ?? null))

  // Wherever the pointer is across the offered surface, the nearest day answers for it.
  const followAt = (clientX: number, surface: HTMLElement) => {
    setPointedX(horizontalAt(clientX, surface))
  }

  // Pulled out of the reading so the listeners below hang off the surface itself: the reading
  // is an object made fresh on every render, and depending on it would tear the listeners down
  // and put them back on each one.
  const interactionRef = reading?.interactionRef

  useEffect(() => {
    const surface = interactionRef?.current
    if (!surface) {
      return
    }
    const followSurface = (event: MouseEvent) => {
      setPointedX(horizontalAt(event.clientX, surface))
    }
    const leaveSurface = () => {
      setPointedX(null)
    }
    surface.addEventListener('mousemove', followSurface)
    surface.addEventListener('mouseleave', leaveSurface)
    return () => {
      surface.removeEventListener('mousemove', followSurface)
      surface.removeEventListener('mouseleave', leaveSurface)
    }
  }, [interactionRef, placed])

  // The chart follows the pointer itself only where it was given a reading to say and no
  // wider surface to read it off.
  const follows = reading !== undefined && interactionRef === undefined

  return (
    <div
      className={className}
      onMouseMove={
        follows
          ? (event) => {
              followAt(event.clientX, event.currentTarget)
            }
          : undefined
      }
      onMouseLeave={
        follows
          ? () => {
              setPointedX(null)
            }
          : undefined
      }
    >
      <svg
        viewBox={`0 0 ${String(BOX)} ${String(BOX)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
        className="h-full w-full"
      >
        <defs>
          {/*
            The wash starts strong right under the line and thins the whole way down to
            nothing at the chart's foot. Reaching nothing is the point: the chart ends where
            the picture over it ends, and a wash still carrying colour at that line would draw
            the hard edge the picture's own fade exists to avoid. Measured against the box
            rather than the shape, so it fades over the chart's height and not the curve's.
          */}
          <linearGradient
            id={`${id}-fill`}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2="0"
            y2={BOX}
          >
            <stop offset="0%" stopColor={color} stopOpacity="0.32" />
            <stop offset="45%" stopColor={color} stopOpacity="0.15" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
          {/*
            How much of the curve has been drawn on, oldest day to newest — the direction it
            was lived in. The wipe carries the wash with it, so what has arrived is always a
            closed shape rather than a line with a gap under it.
          */}
          <clipPath id={`${id}-drawn`}>
            <rect className="trend-draw" x="0" y="0" width={BOX} height={BOX} />
          </clipPath>
        </defs>

        <g clipPath={`url(#${id}-drawn)`}>
          {first && last && (
            <path
              d={`${curve} L ${String(last.x)} ${String(BOX)} L ${String(first.x)} ${String(BOX)} Z`}
              fill={`url(#${id}-fill)`}
            />
          )}

          {/*
            Where the stretch opened, ruled across the whole width. Dotted and faint: it is the
            line the curve is read against rather than a reading of its own, and a solid one
            would be a second curve arguing with the first.
          */}
          {ruleY !== null && (
            <line
              x1={0}
              y1={ruleY}
              x2={BOX}
              y2={ruleY}
              stroke={color}
              strokeOpacity={0.5}
              strokeWidth={1}
              strokeDasharray="2 4"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {rulesOpening && first && (
            <line
              x1={0}
              y1={first.y}
              x2={BOX}
              y2={first.y}
              stroke={color}
              strokeOpacity={0.28}
              strokeWidth={1}
              strokeDasharray="2 4"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/*
            A wide, faint pass under the line and a crisp one over it: the halo is what keeps a
            2.5px stroke from looking cut out of the picture behind it. Both hold their width
            through the box's stretch rather than being smeared with it.
          */}
          <path
            d={drawn.solid}
            fill="none"
            stroke={color}
            strokeOpacity={0.2}
            strokeWidth={7}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {drawn.guessed !== '' && (
            <path
              d={drawn.guessed}
              fill="none"
              stroke={color}
              strokeOpacity={0.5}
              strokeWidth={2}
              strokeDasharray="3 4"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <path
            d={drawn.solid}
            fill="none"
            stroke={color}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      </svg>

      {/*
        And where the line has got to, marked whenever nothing else is being pointed at: the
        newest reading is the one the card is naming, and a curve that simply stops is a curve
        that ran out of room rather than one that arrived somewhere.
      */}
      {marksLatest && !marker && last && (
        <span
          className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: `${String((last.x / BOX) * 100)}%`,
            top: `${String((last.y / BOX) * 100)}%`,
            backgroundColor: color,
            boxShadow: `0 0 0 4px color-mix(in srgb, ${color} 20%, transparent)`,
          }}
        />
      )}

      {/*
        Nothing marks the days until one is asked for, and then only that one — a dot on the
        line says which day without ruling a line down through the wash.
      */}
      {marker && (
        <span
          className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: `${String((marker.x / BOX) * 100)}%`,
            top: `${String((marker.y / BOX) * 100)}%`,
            backgroundColor: color,
            boxShadow: `0 0 0 5px color-mix(in srgb, ${color} 25%, transparent)`,
          }}
        />
      )}

      {/*
        The reading lands in the quiet corner under the oldest — and usually lowest — end of the
        line, well clear of both edges so it never reads as a label stuck to one. It is the only
        place the chart says a number out loud, so it says it large, and it says it in the
        theme's own ink without a scrim: it stands on chrome, not on footage.
      */}
      {hovered && reading && (
        <div
          role="status"
          aria-label="Trend reading"
          className="pointer-events-none absolute left-8"
          style={{ top: `calc(${String(curveBand * 100)}% + 12px)` }}
        >
          <div className="text-lg text-ink-muted">{formatDayLabel(dayStart(hovered.day))}</div>
          <div className="text-4xl leading-tight font-medium text-ink">
            {reading.formatValue(hovered.value)}
          </div>
          <div className="mt-1 text-lg text-ink-muted">{reading.formatPoint(hovered)}</div>
        </div>
      )}
    </div>
  )
}
